import { CircleCheck } from 'lucide-react';

import { Button } from 'components/Button';
import { Card } from 'components/Card';
import {
  BUILT_GROUP_LABEL,
  BUILT_REASON_LABELS,
} from 'components/Kit/BuiltLoads/constants';
import { getBuiltLoadIdentifier } from 'components/Kit/BuiltLoads/utils/getBuiltLoadIdentifier';
import { formatApptDayInStopTz } from 'components/Kit/EmailSuggestions/getValidChangeElements';
import { formatHandledAt } from 'components/Kit/HandledQuotes/utils/formatHandledTooltip';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { MaybeUndef } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { LoadBuildingSuggestions } from 'types/suggestions/LoadBuildingSuggestions';

type BuiltLoadsNoticeProps = {
  suggestions: LoadBuildingSuggestions[];
  onOpenLoad: (loadIdentifier: string) => void;
  onBuildAnother: () => void;
};

const LINK_CLASS =
  'rounded-sm px-0 py-0.5 text-brand-main underline-offset-2 transition-colors duration-150 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-main motion-reduce:transition-none';

// Shown in the Builder instead of an empty form when every load building card on the thread is
// already built. Laid out like LoadCloneRecommendationCard, which takes the same slot: a title,
// each load with its lane, and a text link to build a new load anyway.
export function BuiltLoadsNotice({
  suggestions,
  onOpenLoad,
  onBuildAnother,
}: BuiltLoadsNoticeProps) {
  const isSingle = suggestions.length === 1;

  return (
    <Card className='mt-3 p-3 shadow-none' data-testid='built-loads-notice'>
      <Flex align='start' gap='sm'>
        <CircleCheck
          className='mt-0.5 h-4 w-4 shrink-0 !stroke-success-600'
          aria-hidden
        />
        <div className='min-w-0'>
          <Typography variant='body-sm' weight='semibold'>
            {isSingle
              ? 'This load is already built'
              : `${suggestions.length} loads on this email are already built`}
          </Typography>
          <Typography
            variant='body-xs'
            textColor='lessMuted'
            className='mt-0.5'
          >
            {isSingle
              ? 'Built from this email. Open it to review it.'
              : 'Built from this email. Open one to review it.'}
          </Typography>
        </div>
      </Flex>

      <div className='mt-3 divide-y divide-neutral-300'>
        {suggestions.map((suggestion) => (
          <BuiltLoad
            key={suggestion.id}
            suggestion={suggestion}
            onOpenLoad={onOpenLoad}
          />
        ))}
      </div>

      <Button
        type='button'
        variant='unstyled'
        size='auto'
        className={`mt-3 px-1 text-xs font-medium ${LINK_CLASS}`}
        buttonNamePosthog={ButtonNamePosthog.LoadBuildingBuildAnotherLoad}
        onClick={onBuildAnother}
      >
        Build another load
      </Button>
    </Card>
  );
}

function BuiltLoad({
  suggestion,
  onOpenLoad,
}: {
  suggestion: LoadBuildingSuggestions;
  onOpenLoad: (loadIdentifier: string) => void;
}) {
  const loadIdentifier = getBuiltLoadIdentifier(suggestion);
  const how = suggestion.handledReason
    ? BUILT_REASON_LABELS[suggestion.handledReason]
    : BUILT_GROUP_LABEL;
  const when = formatHandledAt(suggestion.handledAt);
  const { pickup, consignee } = suggestion.suggested ?? {};

  return (
    <div className='space-y-3 py-3 first:pt-0 last:pb-0'>
      <div>
        {loadIdentifier ? (
          <Button
            type='button'
            variant='unstyled'
            size='auto'
            aria-label={`Open load ${loadIdentifier}`}
            className={`min-w-0 whitespace-normal break-words text-sm font-semibold ${LINK_CLASS}`}
            buttonNamePosthog={ButtonNamePosthog.LoadBuildingOpenBuiltLoad}
            onClick={() => onOpenLoad(loadIdentifier)}
          >
            Load {loadIdentifier}
          </Button>
        ) : (
          <Typography variant='body-sm' weight='semibold'>
            Load ID unknown
          </Typography>
        )}
        <Typography variant='body-xs' textColor='lessMuted' className='mt-1'>
          {[how, when].filter(Boolean).join(' · ')}
        </Typography>
      </div>
      <div className='ml-1 border-l border-dashed border-neutral-300 pl-4'>
        <Stop
          label='Pickup'
          place={formatPlace(pickup)}
          day={formatApptDayInStopTz(
            pickup?.apptStartTime ?? pickup?.readyTime,
            pickup?.timezone
          )}
          className='pb-3'
        />
        <Stop
          label='Delivery'
          place={formatPlace(consignee)}
          day={formatApptDayInStopTz(
            consignee?.apptStartTime ?? consignee?.mustDeliver,
            consignee?.timezone
          )}
        />
      </div>
    </div>
  );
}

function Stop({
  label,
  place,
  day,
  className = '',
}: {
  label: string;
  place: string;
  day: string;
  className?: string;
}) {
  return (
    <div className={`relative ${className}`}>
      <span
        aria-hidden='true'
        className='absolute -left-[21px] top-1 h-2 w-2 rounded-full bg-brand-main'
      />
      <Typography variant='body-xs' textColor='lessMuted'>
        {label}
      </Typography>
      <Typography variant='body-xs' className='break-words'>
        {[place || 'Not available', day].filter(Boolean).join(' · ')}
      </Typography>
    </div>
  );
}

function formatPlace(
  stop: MaybeUndef<{ city?: string; state?: string }>
): string {
  return [stop?.city, stop?.state].filter(Boolean).join(', ');
}
